import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  CheckCircle,
  RotateCcw,
  AlertCircle,
  FileText,
  CalendarDays,
  Ticket,
} from "lucide-react";
import Footer from "../Footer/Footer";
import frameBg from "../../Assets/FrameBGDashboard.png";
import NotificationAlert from "../Alerts/NotificationAlert";

import {
  GetReviewPageData,
  MarkReviewed,
  ReopenTicket,
} from "../../Services/ReportIssuesService";

const ReviewTicket = () => {
  const { ticketUID } = useParams();
  const [searchParams] = useSearchParams();

  const email = searchParams.get("email");

  const [ticket, setTicket] = useState(null);
  const [remark, setRemark] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState(null);

  const canReview = ticket?.canReview ?? ticket?.CanReview;

  useEffect(() => {
    fetchTicketData();
  }, []);

  const fetchTicketData = async () => {
    
    try {
      setLoading(true);

      const response = await GetReviewPageData(ticketUID, email);

      if (response?.data?.status === "success") {
        setTicket(response.data.data);
      } else {
        setAlert({
          type: "error",
          message: "Unable to load ticket details.",
        });
      }
    } catch (error) {
      setAlert({
        type: "error",
        message:
          error?.response?.data?.message || "Unable to load ticket details.",
      });
    } finally {
      setLoading(false);
    }
    
  };

  const validateRemark = () => {
    if (!remark.trim()) {
      setAlert({
        type: "error",
        message: "Please provide remarks.",
      });
      return false;
    }

    if (remark.length > 200) {
      setAlert({
        type: "error",
        message: "Remarks cannot exceed 200 characters.",
      });
      return false;
    }

    return true;
  };

  const handleReviewed = async () => {
  if (!validateRemark()) return;

  try {
    setSubmitting(true);

    const payload = {
      TicketUID: ticketUID,
      Email: email,
      Remark: remark,
    };

    const response = await MarkReviewed(payload);

    setAlert({
      type: "success",
      message: response?.data?.message || "Ticket reviewed successfully.",
    });

    setTicket((prev) => ({
      ...prev,
      canReview: false,
      CanReview: false,
      status: 8,
      Status: 8,
    }));
  } catch (error) {
    setAlert({
      type: "error",
      message: error?.response?.data?.message || "Failed to review ticket.",
    });
  } finally {
    setSubmitting(false);
  }
};

const handleReopen = async () => {
  if (!validateRemark()) return;

  try {
    setSubmitting(true);

    const payload = {
      TicketUID: ticketUID,
      Email: email,
      Remark: remark,
    };

    const response = await ReopenTicket(payload);

    setAlert({
      type: "success",
      message: response?.data?.message || "Ticket reopened successfully.",
    });

    setTicket((prev) => ({
      ...prev,
      canReview: false,
      CanReview: false,
      status: 9,
      Status: 9,
    }));
  } catch (error) {
    setAlert({
      type: "error",
      message: error?.response?.data?.message || "Failed to reopen ticket.",
    });
  } finally {
    setSubmitting(false);
  }
};

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin h-10 w-10 border-b-2 border-blue-600 rounded-full mx-auto"></div>
          <p className="mt-3 text-gray-600 dark:text-gray-300">Loading ticket details...</p>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col justify-center items-center px-4">
        <div className="text-center max-w-sm">
          <div className="w-14 h-14 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle size={26} className="text-red-400" />
          </div>
          <h2 className="text-lg font-semibold text-slate-800">
            Invalid Review Link
          </h2>
          <p className="text-sm text-slate-500 mt-2 leading-6">
            We could not process this request. The review link you followed is
            not valid.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen  flex flex-col bg-slate-100 dark:bg-[#2b354e]">      
    <div className="flex-1">
        {/* FULL-WIDTH BG IMAGE STRIP */}
        <div
          className="w-full bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${frameBg})` }}
        >
          <div className="max-w-5xl mx-auto px-6 py-5">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-lg font-semibold text-slate-800 dark:text-white">
                  Ticket Review
                </h2>
               <p className="text-sm text-slate-500 dark:text-slate-200 mt-2 leading-6">
                  Review and confirm completion of the requested work.
                </p>
              </div>
              <span
                className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold border backdrop-blur-sm ${
                  canReview
                    ? "bg-amber-400/20 text-amber-100 border-amber-300/40"
                    : "bg-emerald-400/20 text-emerald-100 border-emerald-300/40"
                }`}
              >
                {canReview ? "Awaiting review" : "Reviewed"}
              </span>
            </div>

            {/* META ROW */}
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-1.5 bg-white/10 rounded-lg px-3 py-1.5 backdrop-blur-sm">
                <Ticket size={13} className="text-white/60" />
                <span className="text-xs text-white/60 uppercase tracking-widest">
                  Ticket
                </span>
                <span className="text-xs font-semibold text-white ml-1">
                  {ticket?.ticketNumber || ticket?.TicketNumber}
                </span>
              </div>
              <div className="flex items-center gap-1.5 bg-white/10 rounded-lg px-3 py-1.5 backdrop-blur-sm">
                <CalendarDays size={13} className="text-white/60" />
                <span className="text-xs text-white/60 uppercase tracking-widest">
                  Raised
                </span>
                <span className="text-xs font-semibold text-white ml-1">
                   {ticket?.raisedAt
    ? new Date(ticket.raisedAt + "Z").toLocaleString("en-US", {
        timeZone: "Asia/Kolkata",
        month: "2-digit",
        day: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    : "--"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* PAGE CONTENT */}
        <div className="max-w-7xl  mx-auto px-6 py-10">
          <div className="grid gap-6 xl:grid-cols-[3.5fr_3.5fr]">
            {/* LEFT — Ticket Details */}
<div className="min-h-[450px] bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm overflow-hidden">         
      <div className="flex items-center gap-2.5 px-6 py-4 border-b border-slate-700 dark:border-slate-700 bg-slate-50 dark:bg-[#1e2638]">
                <FileText size={16} className="text-[#0061A4]" />
                <div>
                 <h3 className="font-semibold text-slate-900 dark:text-white text-sm">
                    Ticket Details
                  </h3>
                 <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    The original request being reviewed.
                  </p>
                </div>
              </div>

              <div className="p-6 space-y-4">
<div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-[#172033] p-4">                  
  <p className="text-xs uppercase tracking-widest text-slate-400 mb-1.5">
                    Title
                  </p>
<p className="text-sm font-semibold text-slate-800 dark:text-slate-100">                 
     {ticket?.title || ticket?.Title}
                  </p>
                </div>

                <div className="px-6 py-5 border-t border-slate-100 dark:border-slate-700">
                  <p className="text-xs uppercase tracking-widest text-slate-400 mb-3">
                    Description
                  </p>
<p className="text-[15px] leading-7 text-slate-600 dark:text-slate-300 whitespace-pre-wrap italic font-medium">                   
   {ticket?.description || ticket?.Description}
                  </p>
                </div>
              </div>
            </div>

            {/* RIGHT — Review */}
            <div className="space-y-4">
<div className="min-h-[450px] bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm overflow-hidden">               
<div className="px-6 py-4 border-b border-slate-700 bg-slate-50 dark:bg-[#172033]">                 
<h3 className="font-semibold text-slate-900 dark:text-white text-sm">                    
  Your Review
                  </h3>
<p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">                    
  Add feedback then approve or reopen.
                  </p>
                </div>

                <div className="p-6 space-y-4">
                  {!canReview && (
                    <div
                      className={`mb-4 flex items-start gap-2.5 rounded-xl border px-4 py-3 ${
                        (ticket?.status || ticket?.Status) === 8
                          ? "border-emerald-200 bg-emerald-50"
                          : "border-blue-200 bg-blue-50"
                      }`}
                    >
                      {(ticket?.status || ticket?.Status) === 8 ? (
                        <CheckCircle
                          size={16}
                          className="text-emerald-600 mt-0.5 shrink-0"
                        />
                      ) : (
                        <AlertCircle
                          size={16}
                          className="text-blue-500 mt-0.5 shrink-0"
                        />
                      )}
                      <div>
                        <p
                          className={`text-xs font-semibold ${
                            (ticket?.status || ticket?.Status) === 8
                              ? "text-emerald-800"
                              : "text-blue-800"
                          }`}
                        >
                          {(ticket?.status || ticket?.Status) === 8
                            ? "Ticket reviewed"
                            : "Under progress"}
                        </p>
                        <p
                          className={`text-xs mt-0.5 leading-5 ${
                            (ticket?.status || ticket?.Status) === 8
                              ? "text-emerald-600"
                              : "text-blue-500"
                          }`}
                        >
                          {(ticket?.status || ticket?.Status) === 8
                            ? "This ticket has been successfully reviewed and is moving forward."
                            : "This ticket has been reopened and is currently being worked on. You will be notified when it is ready for review again."}
                        </p>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Feedback <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      rows={7}
                      maxLength={200}
                      value={remark}
                      disabled={!canReview}
                      onChange={(e) => setRemark(e.target.value)}
                      placeholder="Share your feedback or comments on the completed work..."
className="w-full rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-[#172033] px-4 py-3 text-sm text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-[#00ACED] focus:outline-none focus:ring-4 focus:ring-cyan-100 dark:focus:ring-cyan-900/30 resize-none transition disabled:opacity-60 disabled:cursor-not-allowed" 
/>
                   <div className="mt-1.5 flex justify-end">
                      <p
                        className={`text-xs font-medium ${remark.length > 180 ? "text-orange-500" : "text-slate-400"}`}
                      >
                        {remark.length}/200
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 pt-1">
                    <button
                      onClick={handleReviewed}
                      disabled={submitting || !canReview}
                      className="w-full rounded-xl bg-gradient-to-r from-[#0061A4] to-[#00ACED] px-5 py-3 text-sm font-semibold text-white shadow-md  transition hover:opacity-85 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {submitting ? "Processing..." : "Mark as Reviewed"}
                    </button>

                    <button
                      onClick={handleReopen}
                      disabled={submitting || !canReview}
                      className="w-full rounded-xl border border-red-300 bg-red-200 px-5 py-3 text-sm font-semibold text-red-600 transition hover:bg-red-300 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {submitting ? "Processing..." : "Re-open Ticket"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      {alert && (
        <NotificationAlert
          type={alert.type}
          message={alert.message}
          show={true}
          duration={4000}
          onClose={() => setAlert(null)}
        />
      )}
    </div>
  );
};

export default ReviewTicket;
